// Build-Info provenance card (item 73 Phase B of
// docs/PRODUCT-ROADMAP.md). Renders the per-build provenance an
// operator needs to answer "where did this artifact come from?"
// without leaving the artifact-detail page.
//
// Each declared build expands to three sections:
//
//   1. Source  — VCS revision / branch / repository link / committer
//                principal / build start timestamp.
//   2. Build   — CI agent (Maven, Gradle, GitLab-CI, GitHub-Actions,
//                ...), its version, the deep-link to the CI run, the
//                build name + number used as the pipeline-step proxy.
//   3. Signing — Sigstore subject / issuer / verification status /
//                Rekor entry. The current schema doesn't persist a
//                per-artifact verification record, so this section
//                renders an "unsigned / not recorded" empty state
//                with a pointer to the docs that explain how to
//                enable upload-time Sigstore verification. When a
//                future migration starts populating it, the section
//                will render the real values without the rest of the
//                page changing shape.
//
// Multiple builds can declare the same artifact SHA — typical when a
// CI pipeline re-runs the same release-candidate build. The card
// stacks one expandable row per build, most-recent first; the first
// row is open by default so the common case (one build) reads as a
// single panel rather than a click-to-expand teaser.
//
// The data shape comes straight from `useArtifactBuilds`
// (`/api/v1/artifact-builds/{id}`); item 73 Phase B extends the
// endpoint with `vcs_branch` / `build_url` / `principal` extracted
// from the JFrog Build-Info body JSONB on read so the card doesn't
// have to make a follow-up call to the project-scoped detail
// endpoint just to populate three header fields.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  GitBranch,
  GitCommit,
  Hammer,
  ShieldCheck,
  ShieldOff,
  User,
  Wrench,
} from 'lucide-react';
import { Card, CardBody, CardHeader } from './ui/Card';
import { Badge } from './ui/Badge';
import type { ArtifactBuildSummary } from '../api/buildInfo';

interface Props {
  readonly builds: ArtifactBuildSummary[];
}

function shortRev(rev?: string): string {
  if (!rev) return '';
  return rev.length > 12 ? rev.slice(0, 12) : rev;
}

// Best-effort detector for the CI provider behind a given agent /
// build URL pair. We never leak this into the wire format (the
// backend just stamps the JFrog Build-Info shape), but in the UI it
// surfaces as the "CI system" label so the operator sees
// "GitHub Actions" rather than the raw `buildinfo-go-client/1.0`
// agent name. Falls back to the agent name itself when nothing
// matches.
function ciSystemLabel(agent?: string, buildURL?: string): string {
  const url = (buildURL ?? '').toLowerCase();
  const ag = (agent ?? '').toLowerCase();
  if (url.includes('github.com') && url.includes('/actions/')) return 'GitHub Actions';
  if (url.includes('gitlab.') || ag.includes('gitlab')) return 'GitLab CI';
  if (url.includes('jenkins') || ag.includes('jenkins')) return 'Jenkins';
  if (url.includes('circleci') || ag.includes('circleci')) return 'CircleCI';
  if (url.includes('buildkite') || ag.includes('buildkite')) return 'Buildkite';
  if (url.includes('teamcity') || ag.includes('teamcity')) return 'TeamCity';
  if (url.includes('drone') || ag.includes('drone')) return 'Drone';
  if (url.includes('azure') && url.includes('pipelines')) return 'Azure Pipelines';
  if (ag.includes('bamboo')) return 'Bamboo';
  return agent || 'unknown';
}

interface SectionProps {
  readonly icon: React.ReactNode;
  readonly title: string;
  readonly children: React.ReactNode;
}

function Section({ icon, title, children }: SectionProps) {
  return (
    <div className="space-y-2">
      <p className="orb-section-title text-fg-subtle inline-flex items-center gap-1.5">
        {icon}
        {title}
      </p>
      <dl className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1.5 text-sm">
        {children}
      </dl>
    </div>
  );
}

interface RowProps {
  readonly label: string;
  readonly children: React.ReactNode;
}

function Row({ label, children }: RowProps) {
  return (
    <>
      <dt className="text-xs text-fg-subtle uppercase tracking-wide pt-0.5">{label}</dt>
      <dd className="text-fg break-all">{children}</dd>
    </>
  );
}

function Empty() {
  return <span className="text-fg-subtle">—</span>;
}

interface BuildPanelProps {
  readonly build: ArtifactBuildSummary;
  readonly open: boolean;
  readonly onToggle: () => void;
}

function BuildPanel({ build, open, onToggle }: BuildPanelProps) {
  const buildHref = build.project_id
    ? `/projects/${encodeURIComponent(build.project_id)}/builds/${encodeURIComponent(build.build_name)}/${encodeURIComponent(build.build_number)}`
    : null;
  const ciLabel = ciSystemLabel(build.agent_name, build.build_url);
  return (
    <div className="border border-border rounded-md bg-bg-elevated/30">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-bg-hover/40 transition-colors"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-fg-muted" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-fg-muted" />
        )}
        <Activity className="h-4 w-4 shrink-0 text-accent" />
        <span className="font-medium text-fg">{build.build_name}</span>
        <span className="text-fg-muted font-mono text-sm">#{build.build_number}</span>
        {build.project_slug && <Badge tone="neutral">{build.project_slug}</Badge>}
        <span className="ml-auto text-xs text-fg-muted">
          {new Date(build.started).toLocaleString()}
        </span>
      </button>

      {open && (
        <div className="border-t border-border px-4 py-4 space-y-5">
          {/* Source — where did the bytes come from? */}
          <Section icon={<GitCommit className="h-3.5 w-3.5" />} title="Source">
            <Row label="Commit">
              {build.vcs_revision ? (
                <span className="inline-flex items-center gap-1.5">
                  <code className="orb-code text-xs">{shortRev(build.vcs_revision)}</code>
                  {build.vcs_url && (
                    <a
                      href={build.vcs_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-accent hover:underline inline-flex items-center gap-0.5"
                    >
                      open
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </span>
              ) : (
                <Empty />
              )}
            </Row>
            <Row label="Branch">
              {build.vcs_branch ? (
                <span className="inline-flex items-center gap-1.5">
                  <GitBranch className="h-3.5 w-3.5 text-fg-muted" />
                  <code className="orb-code text-xs">{build.vcs_branch}</code>
                </span>
              ) : (
                <Empty />
              )}
            </Row>
            <Row label="Repository">
              {build.vcs_url ? (
                <a
                  href={build.vcs_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-accent hover:underline inline-flex items-center gap-1"
                >
                  {build.vcs_url}
                  <ExternalLink className="h-3 w-3 shrink-0" />
                </a>
              ) : (
                <Empty />
              )}
            </Row>
            <Row label="Author">
              {build.principal ? (
                <span className="inline-flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5 text-fg-muted" />
                  {build.principal}
                </span>
              ) : (
                <Empty />
              )}
            </Row>
            <Row label="Started">
              <time
                dateTime={build.started}
                title={new Date(build.started).toISOString()}
              >
                {new Date(build.started).toLocaleString()}
              </time>
            </Row>
          </Section>

          {/* Build — which CI run produced the bytes? */}
          <Section icon={<Hammer className="h-3.5 w-3.5" />} title="Build">
            <Row label="CI system">
              <span className="inline-flex items-center gap-1.5">
                <Wrench className="h-3.5 w-3.5 text-fg-muted" />
                {ciLabel}
              </span>
            </Row>
            <Row label="Agent">
              {build.agent_name ? (
                <span>
                  {build.agent_name}
                  {build.agent_version && (
                    <span className="text-fg-subtle"> · {build.agent_version}</span>
                  )}
                </span>
              ) : (
                <Empty />
              )}
            </Row>
            <Row label="Run URL">
              {build.build_url ? (
                <a
                  href={build.build_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-accent hover:underline inline-flex items-center gap-1"
                >
                  {build.build_url}
                  <ExternalLink className="h-3 w-3 shrink-0" />
                </a>
              ) : (
                <Empty />
              )}
            </Row>
            <Row label="Step">
              <span className="font-mono text-xs">
                {build.module_id} → {build.artifact_name}
              </span>
            </Row>
            <Row label="Build">
              {buildHref ? (
                <Link
                  to={buildHref}
                  className="text-accent hover:underline inline-flex items-center gap-1"
                >
                  {build.build_name} #{build.build_number}
                  <ExternalLink className="h-3 w-3 shrink-0" />
                </Link>
              ) : (
                <span className="font-mono text-xs">
                  {build.build_name} #{build.build_number}
                </span>
              )}
            </Row>
          </Section>

          {/* Signing — Sigstore subject + Rekor entry. The current
              schema doesn't persist per-artifact verification, so we
              render an explicit not-recorded state with a pointer at
              the operator-facing docs that wire up Sigstore upload-
              time gates. When a future migration lands the column
              set, this branch picks up the values without page-level
              changes. */}
          <Section icon={<ShieldCheck className="h-3.5 w-3.5" />} title="Signing">
            <Row label="Status">
              <span className="inline-flex items-center gap-1.5 text-fg-muted">
                <ShieldOff className="h-3.5 w-3.5" />
                Not recorded
              </span>
            </Row>
            <Row label="Issuer">
              <Empty />
            </Row>
            <Row label="Identity">
              <Empty />
            </Row>
            <Row label="Rekor entry">
              <Empty />
            </Row>
            <dt />
            <dd className="text-xs text-fg-muted">
              No Sigstore verification record was attached to this build envelope.
              Configure an upload-time policy under{' '}
              <Link to="/admin/trust#sigstore" className="text-accent hover:underline">
                /admin/trust#sigstore
              </Link>{' '}
              to start collecting subject / issuer / Rekor inclusion data on every push.
            </dd>
          </Section>
        </div>
      )}
    </div>
  );
}

export function BuildInfoCard({ builds }: Props) {
  const [openSet, setOpenSet] = useState<Set<string>>(() => {
    // Default: first build expanded so single-build artifacts (the
    // common case) read as one open panel without an extra click.
    return builds.length > 0 ? new Set([builds[0].build_id]) : new Set();
  });

  function toggle(id: string) {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Activity className="h-4 w-4 text-accent" />
            Provenance
          </span>
        }
        description={
          builds.length === 1
            ? 'Produced by one CI build. Source / Build / Signing breakdown below.'
            : `Declared by ${builds.length} CI builds, most recent first. Same artifact SHA across multiple runs is normal for re-built release candidates.`
        }
      />
      <CardBody>
        <div className="space-y-2">
          {builds.map((b) => (
            <BuildPanel
              key={b.build_id}
              build={b}
              open={openSet.has(b.build_id)}
              onToggle={() => toggle(b.build_id)}
            />
          ))}
        </div>
      </CardBody>
    </Card>
  );
}

export default BuildInfoCard;
